// frontend/src/pages/games/TicTacToeGame.tsx

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTicTacToe } from '../../hooks/games/useTicTacToe';
import { useGameSessionTracking } from '../../hooks/games/useGameSessionTracking';
import { TicTacToeBoard } from '../../components/games/tictactoe/TicTacToeBoard';
import { TicTacToeModeSelector } from '../../components/games/tictactoe/TicTacToeModeSelector';
import { GameCompleteModal } from '../../components/games/GameCompleteModal';
import '../../styles/games.css';
import '../../styles/games-tictactoe.css';

const GAME_ID = 'tic-tac-toe';
const WIN_XP = 10;

export function TicTacToeGamePage() {
  const navigate = useNavigate();
  const {
    mode,
    board,
    currentPlayer,
    winner,
    winningLine,
    isDraw,
    isRoundOver,
    score,
    handleCellClick,
    startNewRound,
    setMode,
    resetScore,
  } = useTicTacToe('two-player');

  const { recordCompletion, resetRoundTimer } = useGameSessionTracking(GAME_ID);
  const recordedRef = useRef(false);
  const [xpEarned, setXpEarned] = useState<number | null>(null);

  useEffect(() => {
    if (isRoundOver && !recordedRef.current) {
      recordedRef.current = true;
      // vs-ai modunda sadece insan (X) kazanınca XP verilir; iki oyunculu modda
      // ayrı hesap takibi olmadığından herhangi bir galibiyet XP kazandırır.
      const isHumanWin = winner === 'X';
      const xpAmount = winner && !(mode === 'vs-ai' && winner === 'O') ? WIN_XP : 0;
      const roundScore = winner ? 100 : isDraw ? 50 : 0;
      const { xpAwarded } = recordCompletion({
        score: roundScore,
        cumulativeDeltas: winner === 'X' ? { wins: 1 } : undefined,
        xpAmount,
        session: { isHumanWin },
      });
      setXpEarned(xpAwarded);
    }
    if (!isRoundOver) {
      recordedRef.current = false;
    }
  }, [isRoundOver, winner, isDraw, mode, recordCompletion]);

  const handlePlayAgain = () => {
    resetRoundTimer();
    setXpEarned(null);
    startNewRound();
  };

  const modalEmoji = winner ? (winner === 'X' ? '❌' : '⭕') : '🤝';
  const modalTitle = winner ? '🎉 Oyun Bitti!' : '🤝 Berabere!';
  const modalMessage = winner
    ? mode === 'vs-ai'
      ? winner === 'X'
        ? 'Tebrikler, bilgisayarı yendin!'
        : 'Bilgisayar kazandı — tekrar dene!'
      : `${winner} kazandı!`
    : 'Kimse kazanamadı, berabere bitti!';

  return (
    <div className="game-center-page ttt-game-page">
      <header className="game-center-header">
        <div>
          <h1>❌ Tic Tac Toe</h1>
          <p>Üç işaretini yan yana, alt alta ya da çapraz diz, kazan!</p>
        </div>
        <Link to="/games" className="game-card-btn secondary sm">
          ← Oyun Merkezi
        </Link>
      </header>

      <div className="ttt-toolbar">
        <TicTacToeModeSelector current={mode} onSelect={setMode} />

        <div className="ttt-score">
          <span className="ttt-score-x">❌ {score.X}</span>
          <span className="ttt-score-draw">🤝 {score.draws}</span>
          <span className="ttt-score-o">⭕ {score.O}</span>
        </div>

        <button type="button" className="game-card-btn secondary sm" onClick={resetScore}>
          🔄 Skoru Sıfırla
        </button>
      </div>

      {!isRoundOver && (
        <p className="ttt-turn-indicator">
          Sıra: <strong>{currentPlayer === 'X' ? '❌' : '⭕'}</strong>
          {mode === 'vs-ai' && currentPlayer === 'O' && ' (Bilgisayar düşünüyor...)'}
        </p>
      )}

      <TicTacToeBoard board={board} winningLine={winningLine} onCellClick={handleCellClick} />

      {isRoundOver && (
        <GameCompleteModal
          emoji={modalEmoji}
          title={modalTitle}
          message={modalMessage}
          xpEarned={xpEarned ?? undefined}
          onPlayAgain={handlePlayAgain}
          onReturnToCenter={() => navigate('/games')}
        />
      )}
    </div>
  );
}

